import React, { useEffect, useMemo, useRef, useState } from 'react';
import DateTimePicker from '../DateTimePicker';

/**
 * Shared date entry for tablemaint drawers: the app's DateTimePicker in date-only
 * mode (so it gets the numeric-entry shortcuts), portaled into the enclosing
 * native <dialog> so the calendar isn't clipped by the drawer's top layer, and
 * `autoFlip`ped so a field near the bottom of the drawer (the Fuel Surcharge
 * Tariff's new-line row) opens its calendar upward instead of off-screen.
 * `value`/`onChange` are 'YYYY-MM-DD' strings ('' = empty).
 */
export const parseDate = (v: any): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v == null ? '' : String(v));
  return m && m[1] !== '0000' ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

export const formatDateStr = (d: Date | null): string =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

const DateField: React.FC<{
  label?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
}> = ({ label, value, onChange, disabled, required, invalid }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  useEffect(() => { setPortalEl((wrapRef.current?.closest('dialog') as HTMLElement | null) ?? null); }, []);
  const dateVal = useMemo(() => parseDate(value), [value]);
  return (
    <div ref={wrapRef}>
      {label && (
        <label className="block text-sm font-medium text-label mb-1">
          {label}{required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <DateTimePicker
        mode="date" selectionType="single" inputVariant="modal" labelType="none"
        autoApply autoFlip usePortal portalContainer={portalEl}
        value={dateVal} disabled={disabled} error={invalid ? 'Required' : undefined}
        onChange={(val) => onChange(formatDateStr(val instanceof Date ? val : null))}
      />
    </div>
  );
};

export default DateField;
